import React, { useCallback, useEffect, useState } from "react"
import { Table, Select, Pagination, Tag, message, DatePicker } from "antd"
import dayjs from "dayjs"

import Scheduling from "../../Apis/schedulingApi"
import BookMeetingModal from "./BookMeetingModal"
import CancelMeetingModal from "./CancelMeetingModal"
import MeetingDetailModal from "./MeetingDetailModal"
import {
  BOOKING_STATUS_TAGS,
  canCancel,
  displayStatus,
  errorTextFrom,
  formatDate,
  formatTimeRange,
  isUnresolved,
} from "./schedulingUtils"
import "./Scheduling.css"

const { RangePicker } = DatePicker
const PAGE_SIZE = 10

const MeetingsTab = ({ t, lang, isRTL, settings, onSettingsChanged }) => {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [scope, setScope] = useState("upcoming")
  const [statusFilter, setStatusFilter] = useState("")
  const [range, setRange] = useState({ from: null, to: null })
  const [timezone, setTimezone] = useState(settings?.timezone || "UTC")

  const [detail, setDetail] = useState(null)
  const [cancelTarget, setCancelTarget] = useState(null)
  const [booking, setBooking] = useState(false)

  useEffect(() => {
    if (settings?.timezone) setTimezone(settings.timezone)
  }, [settings?.timezone])

  useEffect(() => {
    setPage(1)
  }, [scope, statusFilter, range.from, range.to])

  const fetchMeetings = useCallback(async () => {
    try {
      setLoading(true)
      const response = await Scheduling.getBookings({
        scope,
        status: statusFilter,
        dateFrom: range.from || "",
        dateTo: range.to || "",
        page,
        pageSize: PAGE_SIZE,
      })
      const data = response?.data || {}
      setRows(Array.isArray(data.results) ? data.results : [])
      setTotal(data.count || 0)
      if (data.timezone) setTimezone(data.timezone)
    } catch (error) {
      message.error(errorTextFrom(t, error, t?.error_load_meetings))
    } finally {
      setLoading(false)
    }
  }, [scope, statusFilter, range.from, range.to, page, t])

  useEffect(() => {
    fetchMeetings()
  }, [fetchMeetings])

  const replaceRow = (updated) => {
    if (!updated) {
      fetchMeetings()
      return
    }
    setRows((previous) => previous.map((row) => (row.id === updated.id ? updated : row)))
    setDetail((previous) => (previous?.id === updated.id ? updated : previous))
  }

  const columns = [
    {
      title: t?.table_attendee || "Attendee",
      dataIndex: "attendee_name",
      render: (value) => value || "—",
    },
    {
      title: t?.table_email || "Email",
      dataIndex: "attendee_email",
      render: (value) => <span dir="ltr">{value}</span>,
    },
    {
      title: t?.table_when || "Date & time",
      dataIndex: "start_at",
      render: (_value, row) => (
        <span>
          {formatDate(row.start_at, timezone, lang)}{" "}
          <span dir="ltr">{formatTimeRange(row.start_at, row.end_at, timezone, lang)}</span>
        </span>
      ),
    },
    {
      title: t?.table_timezone || "Time zone",
      dataIndex: "timezone",
      render: (value) => <span dir="ltr">{value}</span>,
    },
    {
      title: t?.table_status || "Status",
      dataIndex: "status",
      render: (_value, row) => {
        const state = displayStatus(row)
        const tag = BOOKING_STATUS_TAGS[state] || BOOKING_STATUS_TAGS.pending
        return (
          <span className="sch-status-cell">
            <Tag color={tag.color}>{t?.[tag.key] || state}</Tag>
            {/* A link problem is shown separately so a confirmed meeting
                never reads as a failed one. */}
            {row.status === "confirmed" && row.conference_status !== "ready" ? (
              <Tag color="warning">
                {row.conference_status === "failed"
                  ? t?.meet_issue || "Meeting link issue"
                  : t?.meet_pending || "Link being prepared"}
              </Tag>
            ) : null}
          </span>
        )
      },
    },
    {
      title: t?.table_source || "Source",
      dataIndex: "source",
      render: (value) =>
        value === "admin" ? t?.source_admin || "Admin" : t?.source_website || "Website",
    },
    {
      title: t?.table_actions || "Actions",
      key: "actions",
      render: (_value, row) => (
        <span className="sch-row-actions">
          <button type="button" className="sch-link-btn" onClick={() => setDetail(row)}>
            {t?.view || "View"}
          </button>
          {isUnresolved(row) ? (
            <button
              type="button"
              className="sch-link-btn"
              onClick={async () => {
                try {
                  const response = await Scheduling.getBooking(row.id, { refresh: true })
                  replaceRow(response?.data?.booking)
                } catch (error) {
                  message.error(errorTextFrom(t, error, t?.error_generic))
                }
              }}
            >
              {t?.check_status || "Check status"}
            </button>
          ) : null}
          {canCancel(row) ? (
            <button
              type="button"
              className="sch-link-btn is-danger"
              onClick={() => setCancelTarget(row)}
            >
              {t?.cancel || "Cancel"}
            </button>
          ) : (
            <span className="sch-muted-action">{t?.cancel || "Cancel"}</span>
          )}
        </span>
      ),
    },
  ]

  return (
    <div className="sch-tab">
      <div className="sch-card-head">
        <div>
          <h2 className="sch-card-title">{t?.meetings_title || "Meetings"}</h2>
          <p className="sch-muted">
            {(t?.meetings_subtitle || "Times shown in the organizer time zone.") +
              (timezone ? ` (${timezone})` : "")}
          </p>
        </div>
        <button
          type="button"
          className="sch-btn sch-btn-primary"
          disabled={!settings?.bookable}
          title={!settings?.bookable ? t?.booking_paused_hint : undefined}
          onClick={() => setBooking(true)}
        >
          {t?.schedule_meeting || "Schedule meeting"}
        </button>
      </div>

      <div className="sch-filters">
        <Select
          value={scope}
          onChange={setScope}
          className="sch-filter"
          options={[
            { value: "upcoming", label: t?.scope_upcoming || "Upcoming" },
            { value: "past", label: t?.scope_past || "Past" },
            { value: "all", label: t?.scope_all || "All" },
          ]}
        />
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          className="sch-filter"
          options={[
            { value: "", label: t?.status_all || "All statuses" },
            { value: "confirmed", label: t?.status_confirmed || "Confirmed" },
            { value: "pending", label: t?.status_pending || "Booking in progress" },
            { value: "cancellation_pending", label: t?.status_cancellation_pending || "Cancellation pending" },
            { value: "needs_review", label: t?.status_needs_review || "Needs review" },
            { value: "cancelled", label: t?.status_cancelled || "Cancelled" },
            { value: "failed", label: t?.status_failed || "Failed" },
          ]}
        />
        <RangePicker
          className="sch-filter"
          format="YYYY-MM-DD"
          placeholder={[t?.date_start || "From", t?.date_end || "To"]}
          value={range.from && range.to ? [dayjs(range.from), dayjs(range.to)] : null}
          onChange={(dates) => {
            if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) {
              setRange({ from: null, to: null })
              return
            }
            setRange({ from: dates[0].format("YYYY-MM-DD"), to: dates[1].format("YYYY-MM-DD") })
          }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        scroll={{ x: 900 }}
        pagination={false}
        locale={{ emptyText: t?.meetings_empty || "No meetings yet" }}
      />

      {total > 0 ? (
        <div className="d-flex justify-content-end mt-24">
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            showSizeChanger={false}
            showTotal={(count, shown) =>
              isRTL ? `⁦${shown[0]}-${shown[1]}⁩ من ⁦${count}⁩` : `${shown[0]}-${shown[1]} of ${count}`
            }
            onChange={setPage}
          />
        </div>
      ) : null}

      <MeetingDetailModal
        booking={detail}
        open={Boolean(detail)}
        timezone={timezone}
        t={t}
        lang={lang}
        onClose={() => setDetail(null)}
        onChanged={replaceRow}
        onCancelRequest={(row) => {
          setDetail(null)
          setCancelTarget(row)
        }}
      />

      <CancelMeetingModal
        booking={cancelTarget}
        open={Boolean(cancelTarget)}
        timezone={timezone}
        t={t}
        lang={lang}
        onClose={() => setCancelTarget(null)}
        onCancelled={(updated, status) => {
          if (status === "cancellation_pending") {
            message.warning(t?.error_cancellation_pending || "Cancellation is still being confirmed.")
          } else {
            message.success(t?.cancel_success || "Meeting cancelled")
          }
          // Filters are preserved: the list reloads in place.
          fetchMeetings()
          onSettingsChanged?.()
        }}
      />

      <BookMeetingModal
        open={booking}
        organizerTimezone={timezone}
        t={t}
        lang={lang}
        isRTL={isRTL}
        onClose={() => setBooking(false)}
        onBooked={() => {
          message.success(t?.book_success || "Meeting scheduled")
          fetchMeetings()
        }}
      />
    </div>
  )
}

export default MeetingsTab
